import { useCallback, useState } from "react";
import { AxiosInstance } from "axios";
import {
  calculateNewResponse,
  storeComment,
  checkComment,
  listComments,
  deleteComment,
  updateComment,
} from "src/api/comment";
import {
  Message,
  MessageDropdownState,
  CommentDialogState,
  EditCommentDialogState,
} from "./types";

interface UseCommentsOptions {
  api: AxiosInstance;
  orgSlug: string;
  subOrgSlug: string;
  messages: Message[];
  // Called after a comment is stored and the comment filename has been
  // verified — triggers a fresh RAG query to pick up the new admin context.
  resendQuery: (originalMessage: string) => Promise<void>;
}

// Owns the admin-comment workflow: the three-dot dropdown on bot messages,
// the Add Comment dialog (with its "Try comment → suggestion preview → approve"
// flow), and the Edit Comments dialog (list/edit/delete/update with polling).
// Direct port of the handlers previously inlined in Chat.tsx.
export const useComments = ({
  api,
  orgSlug,
  subOrgSlug,
  messages,
  resendQuery,
}: UseCommentsOptions) => {
  const [messageDropdown, setMessageDropdown] = useState<MessageDropdownState>({
    messageId: null,
    position: null,
  });
  const [commentDialog, setCommentDialog] = useState<CommentDialogState>({
    isOpen: false,
    messageId: null,
    originalMessage: "",
    botResponse: "",
    comment: "",
    suggestedResponse: null,
    isLoading: false,
  });
  const [editCommentDialog, setEditCommentDialog] =
    useState<EditCommentDialogState>({
      isOpen: false,
      comments: [],
      isLoading: false,
      showDeleteConfirm: false,
      originalQuery: "",
    });

  const slug = subOrgSlug ? subOrgSlug : orgSlug;

  const pollCommentStatus = useCallback(
    async (filename: string) => {
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds timeout

      while (attempts < maxAttempts) {
        try {
          const response = await checkComment(api, slug, filename);
          if (response.exists) {
            return true;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000));
          attempts++;
        } catch (error) {
          console.error("Error checking comment status:", error);
          return false;
        }
      }
      return false;
    },
    [api, slug],
  );

  const handleDropdownToggle = useCallback(
    (messageId: number, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (messageDropdown.messageId === messageId) {
        setMessageDropdown({ messageId: null, position: null });
      } else {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        setMessageDropdown({
          messageId: messageId,
          position: {
            x: Math.max(10, rect.left),
            y: rect.bottom + scrollTop,
          },
        });
      }
    },
    [messageDropdown.messageId],
  );

  const handleAddComment = useCallback(async () => {
    setCommentDialog((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await calculateNewResponse(
        api,
        slug,
        commentDialog.originalMessage,
        commentDialog.comment,
      );

      setCommentDialog((prev) => ({
        ...prev,
        suggestedResponse: response.preview_response,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error submitting comment:", error);
      setCommentDialog((prev) => ({ ...prev, isLoading: false }));
    }
  }, [api, slug, commentDialog.originalMessage, commentDialog.comment]);

  const handleSubmitFinalComment = useCallback(async () => {
    try {
      setCommentDialog((prev) => ({ ...prev, isLoading: true }));

      const response = await storeComment(
        api,
        slug,
        commentDialog.comment,
        commentDialog.originalMessage,
      );

      const success = await pollCommentStatus(response.filename);

      if (success) {
        setCommentDialog({
          isOpen: false,
          messageId: null,
          originalMessage: "",
          botResponse: "",
          comment: "",
          suggestedResponse: null,
          isLoading: false,
        });

        await resendQuery(commentDialog.originalMessage);
      } else {
        setCommentDialog((prev) => ({
          ...prev,
          isLoading: false,
        }));
        console.error("Comment processing timed out");
      }
    } catch (error) {
      console.error("Error storing comment:", error);
      setCommentDialog((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    api,
    slug,
    commentDialog.comment,
    commentDialog.originalMessage,
    pollCommentStatus,
    resendQuery,
  ]);

  const handleEditCommentsClick = useCallback(async () => {
    setEditCommentDialog((prev) => ({
      ...prev,
      isOpen: true,
      isLoading: true,
    }));
    try {
      const messageId = messageDropdown.messageId;
      if (messageId === null) return;
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      const userMessageIndex = messages.findIndex((m) => m.id === messageId) - 1;
      const userMessage =
        userMessageIndex >= 0 ? messages[userMessageIndex] : null;
      const query = userMessage?.message || "";

      const comments = await listComments(api, slug, query);
      setEditCommentDialog((prev) => ({
        ...prev,
        comments,
        isLoading: false,
        originalQuery: query,
      }));
    } catch (error) {
      console.error("Error fetching comments:", error);
      setEditCommentDialog((prev) => ({ ...prev, isLoading: false }));
    }
  }, [api, slug, messages, messageDropdown.messageId]);

  const handleDropdownClick = useCallback(
    (action: string, messageId: number) => {
      if (action === "Add Comment") {
        const message = messages.find((m) => m.id === messageId);
        if (!message) return;

        const userMessageIndex =
          messages.findIndex((m) => m.id === messageId) - 1;
        const userMessage =
          userMessageIndex >= 0 ? messages[userMessageIndex] : null;

        setCommentDialog({
          isOpen: true,
          messageId: messageId,
          originalMessage: userMessage?.message || "",
          botResponse: message.message,
          comment: "",
          suggestedResponse: null,
          isLoading: false,
        });
      } else if (action === "Edit Comments") {
        handleEditCommentsClick();
      }
      setMessageDropdown({ messageId: null, position: null });
    },
    [messages, handleEditCommentsClick],
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      setEditCommentDialog((prev) => ({ ...prev, isLoading: true }));
      try {
        await deleteComment(api, slug, commentId);

        setMessageDropdown({ messageId: null, position: null });
        setCommentDialog({
          isOpen: false,
          messageId: null,
          originalMessage: "",
          botResponse: "",
          comment: "",
          suggestedResponse: null,
          isLoading: false,
        });
        setEditCommentDialog((prev) => ({
          ...prev,
          isOpen: false,
          isLoading: false,
          showDeleteConfirm: false,
          selectedComment: undefined,
        }));
      } catch (error) {
        console.error("Error deleting comment:", error);
        setEditCommentDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [api, slug],
  );

  const handleUpdateComment = useCallback(
    async (commentId: string, newComment: string) => {
      setEditCommentDialog((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await updateComment(
          api,
          slug,
          commentId,
          editCommentDialog.originalQuery,
          newComment,
        );

        const success = await pollCommentStatus(response.new_filename);

        if (success) {
          const comments = await listComments(
            api,
            slug,
            editCommentDialog.originalQuery,
          );
          setEditCommentDialog((prev) => ({
            ...prev,
            comments,
            isLoading: false,
            selectedComment: undefined,
          }));
        } else {
          setEditCommentDialog((prev) => ({
            ...prev,
            isLoading: false,
          }));
          console.error("Comment update processing timed out");
        }
      } catch (error) {
        console.error("Error updating comment:", error);
        setEditCommentDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [api, slug, editCommentDialog.originalQuery, pollCommentStatus],
  );

  return {
    messageDropdown,
    setMessageDropdown,
    commentDialog,
    setCommentDialog,
    editCommentDialog,
    setEditCommentDialog,
    handleDropdownToggle,
    handleDropdownClick,
    handleAddComment,
    handleSubmitFinalComment,
    handleDeleteComment,
    handleUpdateComment,
  };
};
